import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppBadge, AppMemberDisplay, type BadgeColor } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { InquiryResponse } from 'backend/types'
import {
  INQUIRY_ASSIGN_STATUS_COLOR_MAP,
  INQUIRY_PRIORITY_COLOR_MAP,
  INQUIRY_PRIORITY_LABEL_MAP,
  INQUIRY_RECRUITMENT_TYPE_COLOR_MAP,
  INQUIRY_RECRUITMENT_TYPE_LABEL_MAP,
  INQUIRY_START_ASAP_LABELS,
  type InquiryAssignStatus,
  type InquiryPriority,
  type InquiryRecruitmentType,
} from 'shared'
import { AnoMenuCell } from '@/components/AnoMenuCell'

const INQUIRY_ASSIGN_STATUS_SORT_ORDER: Record<InquiryAssignStatus, number> = {
  取り下げ: 0,
  確定: 1,
  調整中: 2,
  探し中: 3,
}

const INQUIRY_PRIORITY_SORT_ORDER: Record<InquiryPriority, number> = {
  low: 0,
  medium: 1,
  high: 2,
}

const INQUIRY_RECRUITMENT_TYPE_SORT_ORDER: Record<InquiryRecruitmentType, number> = {
  employee_only: 0,
  bp_allowed: 1,
}

/**
 * 期間列のソートキー。昇順で「なる早 → 開始日未定 → 開始日の早い順」に並べる。
 * AppDataTable は accessor の値を < / > で比べるため、先頭 1 文字で区分を表し、日付はその後ろに付ける
 */
function toStartOnSortKey(row: InquiryResponse): string {
  if (row.is_start_asap) return '0'
  if (!row.start_on) return '1'
  return `2${row.start_on}`
}

/**
 * 内容の幅に合わせる列（width: '1%'）で使う。
 * AppDataTable はセルの中身を `whitespace-normal wrap-break-word` の div で包むので、
 * td に nowrap を付けても効かない。内側の div を直接 nowrap にして、最長値の幅まで列を広げる
 */
const NOWRAP_CELL = '[&>div]:whitespace-nowrap'

/** 一覧の日付表示。`2026-04-01` を `2026/04/01` にする */
function formatDate(date: string): string {
  return date.replaceAll('-', '/')
}

/**
 * 社員番号バッジ + 氏名の縦積み（SMG・営業で共通）。
 * AppMemberDisplay の縦積みは番号と名前の間に余白が無いので、ルートの flex に gap を足す
 */
function renderMember(employeeNumber: number | null, name: string | null) {
  return (
    <div className='[&>div]:gap-1'>
      <AppMemberDisplay employeeNumber={employeeNumber} name={name} direction='vertical' nameSize='small' />
    </div>
  )
}

export const columns: AppDataTableColumn<InquiryResponse>[] = [
  {
    key: 'deal_info',
    label: '案件情報',
    width: '250px',
    accessor: (row) => row.customer_name ?? '',
    render: (row) => (
      <div className='space-y-2'>
        <div>
          <div className='text-xs text-muted-foreground'>{row.customer_name ?? '-'}</div>
          <div className='font-medium'>{row.deal_name ?? '-'}</div>
        </div>
        <div className='flex items-center gap-1'>
          <span className='text-xs text-muted-foreground'>ANO :</span>
          <AnoMenuCell ano={row.ano} salesforceProjectId={row.salesforce_project_id} />
        </div>
      </div>
    ),
    required: true,
    active: true,
  },
  {
    key: 'inquiry_assign_status_name',
    label: 'ステータス',
    width: '80px',
    accessor: (row) =>
      row.inquiry_assign_status_name ? INQUIRY_ASSIGN_STATUS_SORT_ORDER[row.inquiry_assign_status_name] : -1,
    render: (row) =>
      row.inquiry_assign_status_name ? (
        <AppBadge color={INQUIRY_ASSIGN_STATUS_COLOR_MAP[row.inquiry_assign_status_name] as BadgeColor}>
          {row.inquiry_assign_status_name}
        </AppBadge>
      ) : (
        '-'
      ),
    active: true,
  },
  {
    key: 'short_primary_division_name',
    label: '主管部門',
    width: '100px',
    accessor: (row) => row.short_primary_division_name ?? '-',
    active: true,
  },
  {
    key: 'industry_name',
    label: '業界',
    // 固定幅だと「官公庁・自治体・団体」が切れる。1% にして最長値に合わせ、余りは案件情報に回す
    width: '1%',
    cellClassName: NOWRAP_CELL,
    accessor: (row) => row.industry_name ?? '-',
    active: true,
  },
  {
    key: 'service_manager_name',
    label: 'SMG',
    width: '1%',
    cellClassName: NOWRAP_CELL,
    accessor: (row) => row.service_manager_name ?? '-',
    render: (row) => renderMember(row.service_manager_employee_number, row.service_manager_name),
    active: true,
  },
  {
    key: 'sales_member_name',
    label: '営業',
    width: '1%',
    cellClassName: NOWRAP_CELL,
    accessor: (row) => row.sales_member_name ?? '-',
    render: (row) => renderMember(row.sales_member_employee_number, row.sales_member_name),
    active: true,
  },
  {
    key: 'start_on',
    label: '期間',
    width: '1%',
    cellClassName: NOWRAP_CELL,
    accessor: toStartOnSortKey,
    render: (row) => {
      if (!row.is_start_asap && !row.start_on && !row.end_on) return <span>未定</span>
      const formattedStartOn = row.start_on ? formatDate(row.start_on) : '未定'
      const start = row.is_start_asap ? INQUIRY_START_ASAP_LABELS.LABEL : formattedStartOn
      const end = row.end_on ? formatDate(row.end_on) : '未定'
      // 横幅を詰めるため開始と終了を 2 行に分ける。
      // 等幅数字で上下の桁を揃え、「~」は薄くして日付だけが目に入るようにする
      return (
        <div className='flex flex-col tabular-nums'>
          <span>
            {start}
            <span className='text-muted-foreground'> ~</span>
          </span>
          <span>{end}</span>
        </div>
      )
    },
    active: true,
  },
  {
    key: 'estimated_amount',
    label: '想定金額',
    // table-layout: auto で幅 1% にすると、列は一番桁数の多い値の幅に収まり、余った幅は他の列に回る
    width: '1%',
    cellClassName: NOWRAP_CELL,
    accessor: (row) => row.estimated_amount ?? Number.NEGATIVE_INFINITY,
    render: (row) => {
      if (row.estimated_amount == null) return <div className='text-right'>-</div>
      return <div className='text-right'>{Math.round(row.estimated_amount / 1000).toLocaleString()}千円</div>
    },
    active: true,
  },
  {
    key: 'priority',
    label: '優先度',
    width: '80px',
    accessor: (row) => INQUIRY_PRIORITY_SORT_ORDER[row.priority],
    render: (row) => (
      <AppBadge color={INQUIRY_PRIORITY_COLOR_MAP[row.priority] as BadgeColor}>
        {INQUIRY_PRIORITY_LABEL_MAP[row.priority]}
      </AppBadge>
    ),
    active: true,
  },
  {
    key: 'recruitment_type',
    label: '募集形態',
    width: '100px',
    accessor: (row) =>
      row.recruitment_type ? INQUIRY_RECRUITMENT_TYPE_SORT_ORDER[row.recruitment_type] : Number.POSITIVE_INFINITY,
    render: (row) => {
      if (!row.recruitment_type) return <span>-</span>
      return (
        <AppBadge color={INQUIRY_RECRUITMENT_TYPE_COLOR_MAP[row.recruitment_type]}>
          {INQUIRY_RECRUITMENT_TYPE_LABEL_MAP[row.recruitment_type]}
        </AppBadge>
      )
    },
    active: true,
  },
]
